/**
 * Safe DOM utilities for the editor
 * 
 * These helpers prevent null reference errors when elements don't exist
 * (e.g., in embedded mode where some UI is hidden)
 */

/**
 * Get element by ID, returns null if not found
 */
export function $(id: string): HTMLElement | null {
  return document.getElementById(id);
}

/**
 * Resolve ID string or element to element
 */
function resolveElement(idOrElement: string | HTMLElement | null): HTMLElement | null {
  if (idOrElement === null) return null;
  if (typeof idOrElement === 'string') {
    return document.getElementById(idOrElement);
  }
  return idOrElement;
}

/**
 * Safely set display style
 */
export function setDisplay(idOrElement: string | HTMLElement | null, value: string): void {
  const el = resolveElement(idOrElement);
  if (el) el.style.display = value;
}

/**
 * Safely set text content
 */
export function setText(idOrElement: string | HTMLElement | null, value: string | number): void {
  const el = resolveElement(idOrElement);
  if (el) el.textContent = String(value);
}

/**
 * Safely set input value
 */
export function setValue(idOrElement: string | HTMLInputElement | null, value: string | number): void {
  const el = resolveElement(idOrElement as string | HTMLElement | null) as HTMLInputElement | null;
  if (el && 'value' in el) el.value = String(value);
}

/**
 * Safely set checkbox checked state
 */
export function setChecked(idOrElement: string | HTMLInputElement | null, value: boolean): void {
  const el = resolveElement(idOrElement as string | HTMLElement | null) as HTMLInputElement | null;
  if (el && 'checked' in el) el.checked = value;
}

/**
 * Safely set innerHTML
 */
export function setHTML(idOrElement: string | HTMLElement | null, value: string): void {
  const el = resolveElement(idOrElement);
  if (el) el.innerHTML = value;
}

/**
 * Safely add event listener
 */
export function on(idOrElement: string | HTMLElement | null, event: string, handler: EventListener): void {
  const el = resolveElement(idOrElement);
  if (el) el.addEventListener(event, handler);
}

/**
 * Safely set style property
 */
export function setStyle(idOrElement: string | HTMLElement | null, property: string, value: string): void {
  const el = resolveElement(idOrElement);
  if (el) el.style.setProperty(property, value);
}

/**
 * Safely add/remove class
 */
export function addClass(idOrElement: string | HTMLElement | null, className: string): void {
  const el = resolveElement(idOrElement);
  if (el) el.classList.add(className);
}

export function removeClass(idOrElement: string | HTMLElement | null, className: string): void {
  const el = resolveElement(idOrElement);
  if (el) el.classList.remove(className);
}

/**
 * Safely toggle disabled state
 */
export function setDisabled(idOrElement: string | HTMLButtonElement | null, disabled: boolean): void {
  const el = resolveElement(idOrElement as string | HTMLElement | null) as HTMLButtonElement | null;
  if (el && 'disabled' in el) el.disabled = disabled;
}

/**
 * Convert hex color string to integer
 */
export function hexToInt(hex: string): number {
  return parseInt(hex.replace('#', ''), 16);
}

/**
 * Convert integer color to hex string
 */
export function intToHex(color: number): string {
  return '#' + color.toString(16).padStart(6, '0');
}
